import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { imgSrc } from "@/lib/api";
import { ShareButtons } from "@/components/site/ShareButtons";
import { FavButton } from "@/components/site/FavButton";
import { CurrencySwitcher } from "@/components/site/CurrencySwitcher";
import { Price, parseMoney } from "@/context/CurrencyContext";
import { FileDown } from "lucide-react";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { WhatsAppButton } from "@/components/site/Contact";
import { useItem, usePageMeta } from "@/hooks/useData";

export const Gallery = ({ images, title, dark }) => {
  const [active, setActive] = useState(0);
  if (!images?.length) return <div className={`aspect-[4/3] ${dark ? "bg-gw-mid" : "bg-gw-card"}`} />;
  return (
    <div data-testid="detail-gallery">
      <figure className="relative overflow-hidden aspect-[4/3] m-0">
        <img key={active} src={imgSrc(images[active])} alt={title} className="page-in w-full h-full object-cover" data-testid="detail-main-image" />
      </figure>
      {images.length > 1 && (
        <div className="grid grid-cols-5 gap-2 mt-2">
          {images.map((src, i) => (
            <button key={src + i} type="button" onClick={() => setActive(i)} data-testid={`detail-thumb-${i}`}
              className={`aspect-square overflow-hidden border-2 transition-opacity duration-200 ${i === active ? "border-current opacity-100" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={imgSrc(src)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Facts = ({ rows }) => (
  <dl className="grid grid-cols-2 border-t border-current/20 mt-8" data-testid="detail-facts">
    {rows.filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
      <div key={k} className="py-4 border-b border-current/20 pr-4">
        <dt className="lab opacity-60">{k}</dt>
        <dd className="lux text-xl mt-1">{v}</dd>
      </div>
    ))}
  </dl>
);

function Detail({ kind }) {
  const { id } = useParams();
  const isProperty = kind === "properties";
  const item = useItem(kind, id);
  const dark = !isProperty;
  const back = isProperty ? { to: "/realty", label: "Wolfe Realtors" } : { to: "/development", label: "Grey Wolfe Development" };
  usePageMeta(item ? `${item.title}${item.location ? `, ${item.location}` : ""} | ${back.label}` : back.label,
    item ? [item.listing_type || item.status, item.description].filter(Boolean).join(" — ").slice(0, 158) : undefined,
    item ? `https://www.greywolfeng.com/api/share/${isProperty ? "property" : "project"}/${id}/image.jpg` : undefined);

  const wrap = dark ? "bg-gw-ground text-white" : "bg-gw-paper text-gw-ink";
  if (item === undefined) return <section className={`${wrap} min-h-[70vh]`} />;
  if (item === null)
    return (
      <section className={`${wrap} min-h-[70vh] py-32`} data-testid="detail-not-found">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="lux gw-h2">This listing is no longer available.</h1>
          <Link to={back.to} className="lab mt-8 inline-block underline underline-offset-4">← Back to {back.label}</Link>
        </div>
      </section>
    );

  const rows = isProperty
    ? [["Listing", item.listing_type], ["Type", item.property_type], ["Price", item.price], ["Location", item.location],
       ["Bedrooms", item.bedrooms], ["Bathrooms", item.bathrooms], ["Size", item.area], ["Status", item.status]]
    : [["Status", item.status], ["Category", item.category], ["Location", item.location], ["Units", item.units], ["Completion", item.completion]];
  const bullets = isProperty ? item.features : item.highlights;

  return (
    <div className="page-in">
      <section className={`${wrap} py-16 md:py-24 border-t-8 ${dark ? "border-white" : "border-gw-rule"}`} data-testid="detail-page">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`flex flex-wrap justify-between gap-4 lab border-b pb-4 ${dark ? "border-white/25 text-gw-mist" : "border-gw-rule/25"}`}>
            <Link to={back.to} data-testid="detail-back-link" className="hover:underline underline-offset-4">← {back.label}</Link>
            {item.is_sample && <span data-testid="detail-sample-note">Sample listing — replace from admin</span>}
          </div>
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 mt-12">
            <Gallery images={item.images} title={item.title} dark={dark} />
            <div className="min-w-0">
              <p className="lab opacity-70">{isProperty ? item.listing_type : item.status}</p>
              <h1 className="lux gw-h2 mt-3" data-testid="detail-title">{item.title}</h1>
              {isProperty && item.price && (
                <div className="mt-4 flex flex-wrap items-end gap-6">
                  <Price price={item.price} className="lux text-3xl" testId="detail-price" />
                  {parseMoney(item.price) && <CurrencySwitcher dark={dark} testPrefix="detail-currency" />}
                </div>
              )}
              <Facts rows={rows} />
              {item.description && <p className="gw-body mt-8 whitespace-pre-line" data-testid="detail-description">{item.description}</p>}
              {bullets?.length > 0 && (
                <ul className="mt-6 space-y-2">
                  {bullets.map((b) => <li key={b} className="flex gap-3"><span className="opacity-50">—</span>{b}</li>)}
                </ul>
              )}
              {item.brochure && (
                <a href={imgSrc(item.brochure)} target="_blank" rel="noopener noreferrer" download={item.brochure_name || "brochure.pdf"}
                  data-testid="detail-brochure-button"
                  className={`mt-8 group lab inline-flex items-center gap-3 px-6 py-4 transition-colors duration-200 ${dark ? "bg-white text-gw-onwhite hover:bg-gw-mist" : "bg-gw-btn text-gw-btnfg hover:bg-gw-btnhover"}`}>
                  <FileDown size={16} /> Download brochure (PDF)
                </a>
              )}
              {isProperty ? (
                <div className="flex flex-wrap items-center gap-4">
                  <ShareButtons kind="property" id={item.id} className="mt-8" testPrefix="detail-share"
                    text={`${item.title}${item.price ? ` · ${item.price}` : ""} — Wolfe Realtors`} />
                  <FavButton id={item.id} title={item.title} testId="detail-fav-button" className="mt-8 w-10 h-10 border border-current/30" />
                </div>
              ) : (
                <ShareButtons kind="project" id={item.id} className="mt-8" testPrefix="detail-share"
                  text={`${item.title} — Grey Wolfe Development`} />
              )}
            </div>
          </div>
        </div>
      </section>
      <section id="contact" className={`${dark ? "bg-gw-deep text-white" : "bg-gw-paper2 text-gw-ink"} py-20 md:py-28 scroll-mt-16`} data-testid="detail-enquiry-section">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          <div>
            <p className="lab opacity-70">Enquire</p>
            <h2 className="lux gw-h2 mt-4">Ask about {item.title}.</h2>
            <p className="gw-body mt-6 max-w-md opacity-80">{isProperty ? "Book a viewing, ask for the full details or talk through the numbers." : "Arrange a site visit or ask about units and timelines."}</p>
            <WhatsAppButton testId="detail-whatsapp-button" text={`Hello, I'm interested in ${item.title}.`}
              className={`mt-8 lab px-6 py-4 transition-colors duration-200 ${dark ? "bg-white text-gw-onwhite hover:bg-gw-mist" : "bg-gw-btn text-gw-btnfg hover:bg-gw-btnhover"}`} />
          </div>
          <EnquiryForm light={!dark} testPrefix="detail-enquiry"
            defaultCompany={isProperty ? "Wolfe Realtors" : "Grey Wolfe Development"}
            reference={{ type: isProperty ? "property" : "project", id: item.id, title: item.title }} />
        </div>
      </section>
    </div>
  );
}

export const PropertyDetail = () => <Detail kind="properties" />;
export const ProjectDetail = () => <Detail kind="projects" />;
